import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuItem, Popper, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Autocomplete from "@mui/material/Autocomplete";

import { MENU_ITEM, SVG } from "../../consts/sentryIds";
import { palette } from "../../theme/palette";

import { setDriverManagementReloadModalOpen } from "../../reducers/ownApp";
import { showReloadDriverManagementSelector } from "../../reducers/ownApp/selectors";

import { CheckmarkIcon, MapLocationIcon, ReloadIcon } from "../Icons";

const useStyles = makeStyles(() => ({
  container: {
    minWidth: "10.938rem",
    maxWidth: "10.938rem",
  },
  popper: {
    minWidth: "10.938rem !important",
  },
  iconContainer: {
    width: "1rem",
    paddingLeft: "0.625rem",
  },
  mapLocationIcon: {
    width: "0.813rem",
    height: "1rem",
    fill: "white",
  },
  reloadIcon: {
    width: "1rem",
    height: "1rem",
    fill: "white",
  },
  selectItem: {
    height: "1.5rem",
    width: "100%",
    display: "flex",
    fontSize: "0.875rem",
    justifyContent: "space-between",
  },
  popupIndicatorOpen: {
    transform: "rotate(0)",
  },
  inputRoot: {
    marginTop: "0.063rem",
  },
  input: {
    textAlign: "right",
    fontSize: "0.75rem",
    color: "white",

    "&::selection": {
      background: palette.typography.white.disabled,
    },
  },
  endAdornment: {
    marginTop: "-1%",
  },
  option: {
    padding: 0,
  },
  noOption: {
    padding: "0.5rem 1rem",
  },
  checkMarkIcon: {
    width: "1rem",
    height: "0.75rem",
    cursor: "default",
  },
}));

export const Dropdown = ({
  value,
  items = [],
  onChange,
  placeholder,
  id,
  disabled,
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const showReloadDriverManagement = useSelector(
    showReloadDriverManagementSelector
  );

  return (
    <Autocomplete
      id={id}
      sentryId={id}
      options={items}
      blurOnSelect
      disabled={disabled}
      value={value}
      PopperComponent={({ ...props }) => (
        <Popper
          {...props}
          style={{ marginTop: "-0.22rem" }}
          placement="bottom-end"
        />
      )}
      onChange={(e, option) => {
        value !== option.value && onChange(option.value);
      }}
      popupIcon={
        !showReloadDriverManagement ? (
          <MapLocationIcon
            sentryId={`${id}__${SVG}`}
            className={styles.mapLocationIcon}
          />
        ) : (
          <ReloadIcon
            className={styles.reloadIcon}
            onClick={() => dispatch(setDriverManagementReloadModalOpen(true))}
          />
        )
      }
      classes={{
        root: styles.container,
        popupIndicatorOpen: styles.popupIndicatorOpen,
        inputRoot: styles.inputRoot,
        input: styles.input,
        endAdornment: styles.endAdornment,
        option: styles.option,
        noOptions: styles.noOption,
        popper: styles.popper,
      }}
      disableClearable
      isOptionEqualToValue={(option, value) => {
        return option.value === value;
      }}
      getOptionLabel={(option) => {
        if (!option) return "";

        return typeof option === "string"
          ? items.find(({ value: optionValue }) => value === optionValue)?.label
          : option.label ?? null;
      }}
      renderOption={(props, { value: itemValue, label }) => (
        <MenuItem
          {...props}
          key={itemValue}
          className={styles.selectItem}
          sentryId={id && `${id}__${MENU_ITEM}`}
        >
          {label}
          <div className={styles.iconContainer}>
            {value === itemValue && (
              <CheckmarkIcon className={styles.checkMarkIcon} />
            )}
          </div>
        </MenuItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            "data-testid": "autocomplete-input",
          }}
        />
      )}
    />
  );
};
